import { Instance, SnapshotIn, types } from 'mobx-state-tree'
import { AppConfig } from 'store/modules/config'
import { baseModel } from 'store/utils/base-model'
import type { CheckinPerformanceQuestion } from '../checkin-performance-questions'
import { CheckinSkill } from '../checkin-skills'
import { createStore } from 'store/utils/create-store'
import { DateType } from 'store/utils/date-type'
import { getRootStore } from 'store/utils/get-root-store'
import { Position } from '../positions'
import { reference } from 'store/utils/reference'
import { Skill } from '../skills'
import { User } from '../users'

export type RatingScaleLabel = {
  labelName: string
  labelSlug: string
  color: string
  scaleValue: number
  description: string
}

export const Checkin = baseModel('checkins')
  .props({
    archivedAt: types.maybeNull(DateType),
    assessor: reference(User),
    assessorApprovedAt: types.maybeNull(DateType),
    author: reference(User, { required: true }),
    createdAt: DateType,
    description: types.maybeNull(types.string),
    finalisedAt: types.maybeNull(DateType),
    completedAt: types.maybeNull(DateType),
    notes: types.maybeNull(types.string),
    position: reference(Position),
    positionName: types.maybeNull(types.string),
    previousRoleName: types.maybeNull(types.string),
    slug: types.string,
    status: types.union(
      types.literal('not_started'),
      types.literal('in_progress'),
      types.literal('pending_assessor'),
      types.literal('assessor_in_progress'),
      types.literal('pending_review'),
      types.literal('review_in_progress'),
      types.literal('finalised'),
    ),
    ratingScaleLabels: types.optional(types.frozen<RatingScaleLabel[]>(), []),
    summaryFinalLevelTotal: types.maybeNull(types.number),
    summaryPercentageDifference: types.maybeNull(types.number),
    summaryRequiredLevelTotal: types.maybeNull(types.number),
    summaryStatus: types.maybeNull(types.string),
    title: types.maybeNull(types.string),
    jointReview: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    setFinalisedAt(finalisedAt: Date) {
      self.finalisedAt = finalisedAt
    },
  }))
  .views((self) => ({
    // Single point of truth: finds the label that matches a status value.
    // summary_data stores label_slug (e.g. "working_towards"), so we try
    // an exact slug match first, then fall back to case-insensitive name
    // match for backward compatibility.
    findLabelByStatus(status: string | null): RatingScaleLabel | undefined {
      if (!status || !self.ratingScaleLabels?.length) return undefined
      return (
        self.ratingScaleLabels.find((l) => l.labelSlug === status) ??
        self.ratingScaleLabels.find(
          (l) => l.labelName?.toLowerCase() === status.toLowerCase(),
        )
      )
    },

    // Returns the hex color for a given status slug or name.
    labelColorForStatus(status: string | null): string | null {
      return this.findLabelByStatus(status)?.color ?? null
    },

    get summaryScaleValue(): number | null {
      return this.findLabelByStatus(self.summaryStatus)?.scaleValue ?? null
    },

    get summaryStatusColor(): string | null {
      return this.labelColorForStatus(self.summaryStatus)
    },

    // Returns the human-readable label name for the current summaryStatus.
    get summaryStatusLabel(): string | null {
      return this.findLabelByStatus(self.summaryStatus)?.labelName ?? null
    },

    // Returns the labelSlug for a given numeric delta, clamped to the
    // available scale range when no exact match exists.
    labelSlugForDelta(delta: number): string {
      const labels = self.ratingScaleLabels ?? []
      if (!labels.length) return ''

      const sorted = [...labels].sort((a, b) => a.scaleValue - b.scaleValue)
      const exact = sorted.find((l) => l.scaleValue === delta)
      if (exact) return exact.labelSlug

      if (delta < sorted[0].scaleValue) return sorted[0].labelSlug
      if (delta > sorted[sorted.length - 1].scaleValue)
        return sorted[sorted.length - 1].labelSlug

      // Shouldn't be reached for integer deltas and scale values, but guard
      return sorted.reduce((closest, l) =>
        Math.abs(l.scaleValue - delta) < Math.abs(closest.scaleValue - delta)
          ? l
          : closest,
      ).labelSlug
    },

    labelNameForDelta(delta: number): string | null {
      return (
        self.ratingScaleLabels?.find((l) => l.scaleValue === delta)
          ?.labelName ?? null
      )
    },

    colorForDelta(delta: number): string | null {
      return (
        self.ratingScaleLabels?.find((l) => l.scaleValue === delta)?.color ??
        null
      )
    },

    get checkinSkills(): CheckinSkill[] {
      return getRootStore(self).checkinSkills.filtered(
        (checkinSkill) => checkinSkill.checkin?.id === self.id,
      )
    },
    get checkinPerformanceQuestions(): CheckinPerformanceQuestion[] {
      return getRootStore(self)
        .checkinPerformanceQuestions.filtered(
          (question) => question.checkinId === self.id,
        )
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    },
    get hasPerformanceQuestions(): boolean {
      return this.checkinPerformanceQuestions.length > 0
    },
    get isArchived() {
      return self.archivedAt !== null
    },
    get isFinalised() {
      return self.finalisedAt !== null
    },
    get isLastFinalisedForAuthor(): boolean {
      const lastFinalised = getRootStore(
        self,
      ).checkins.lastFinalisedUserCheckin(self.author.id)

      return lastFinalised?.id === self.id
    },
    get path() {
      return `/checkins/${self.slug}`
    },
    get selectedCheckinSkills(): CheckinSkill[] {
      return this.checkinSkills.filter(
        (checkinSkill) => !checkinSkill.initiallySkipped,
      )
    },
    get skills(): Skill[] {
      return this.selectedCheckinSkills.map(
        (checkinSkill) => checkinSkill.skill,
      )
    },
    get userIds() {
      return this.users.map((user) => user.id)
    },
    get users() {
      const users = [self.author]
      if (self.assessor) users.push(self.assessor)

      return users
    },
    actionNeededFromUser(userId: string) {
      if (self.status === 'in_progress' || self.status === 'not_started') {
        return self.author.id === userId
      }
      if (
        self.status === 'assessor_in_progress' ||
        self.status === 'pending_assessor'
      ) {
        return !!(self.assessor && self.assessor.id === userId)
      }
      return false
    },
    titleWithFallback(userId: string) {
      return (
        self.title ||
        `${
          userId === self.author.id ? 'Your' : `${self.author.fname}'s`
        } Check-in${self.positionName ? ` against ${self.positionName}` : ''}`
      )
    },
  }))

export type CheckinModelAttributes = SnapshotIn<typeof Checkin>

export interface Checkin extends Instance<typeof Checkin> {}

type CheckinCreateAttributes = {
  assessor: string
  author: string
  description?: string | null
  initiated_by: 'assessor' | 'self'
  position?: string | null
  skill_ids?: string[]
  title?: string | null
}

type CheckinUpdateAttributes = {
  assessor?: string
  notes?: string
}

export type CheckinFilters = {
  archived?: true
  assessor_or_author_id?: string | string[]
  author_id?: string | string[]
  author_state?: string | string[]
  created_at_from?: string
  created_at_to?: string
  last_finalised?: true
  org_id?: string | string[]
  status?: string | string[]
  updated_since?: Date | null
}

type CheckinIncludes =
  | 'assessor'
  | 'author'
  | 'checkin_skills'
  | 'checkin_skills.skill'
  | 'checkin_skills.attachments'
  | 'checkin_performance_questions'

type CheckinStoreOptions = {
  CreateAttributes: CheckinCreateAttributes
  Filter: CheckinFilters
  Include: CheckinIncludes[]
  UpdateAttributes: CheckinUpdateAttributes
}

export const CheckinStore = createStore<typeof Checkin, CheckinStoreOptions>(
  'Checkin',
  Checkin,
)
  .actions((store) => ({
    fetchLastFinalisedUserCheckin(userId: string) {
      return store.fetchAll({
        filter: {
          author_id: userId,
          status: ['finalised'],
        },
        include: [
          'assessor',
          'author',
          'checkin_skills',
          'checkin_skills.skill',
        ],
        page: { size: 1 },
      })
    },
    async archive(id: string) {
      const checkin = store.byId(id)
      if (!checkin) return { success: false }

      try {
        const response = await fetch(
          `${AppConfig.apiUrl}/api/v1/checkins/${checkin.slug}/archive`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/vnd.api+json' },
            credentials: 'include',
          },
        )

        if (!response.ok) return { success: false }

        try {
          store.unload(id)
        } catch {
          /* reference resolution errors during re-render */
        }
        return { success: true }
      } catch {
        return { success: false }
      }
    },
    async bulkArchive(ids: string[]) {
      try {
        const response = await fetch(
          `${AppConfig.apiUrl}/api/v1/checkins/bulk_archive`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/vnd.api+json' },
            credentials: 'include',
            body: JSON.stringify({ ids }),
          },
        )

        if (!response.ok) return { success: false }

        ids.forEach((id) => {
          try {
            store.unload(id)
          } catch {
            /* reference resolution errors during re-render */
          }
        })
        return { success: true }
      } catch {
        return { success: false }
      }
    },
    async bulkDestroy(ids: string[]) {
      try {
        const response = await fetch(
          `${AppConfig.apiUrl}/api/v1/checkins/bulk_destroy`,
          {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/vnd.api+json' },
            credentials: 'include',
            body: JSON.stringify({ ids }),
          },
        )

        if (!response.ok) return { success: false }

        ids.forEach((id) => {
          try {
            store.unload(id)
          } catch {
            /* reference resolution errors during re-render */
          }
        })
        return { success: true }
      } catch {
        return { success: false }
      }
    },
  }))
  .views((store) => ({
    get finalised() {
      return store.filtered((checkin) => checkin.isFinalised)
    },
    get forNonArchivedUsers() {
      return store.filtered(
        (checkin) => checkin.author.isActive && !checkin.isArchived,
      )
    },
    nonFinalisedSortedByUpdatedAtDescWithLimitForUser(
      limit: number,
      userId: string,
    ) {
      return store
        .filtered(
          (checkin) =>
            checkin.status !== 'finalised' && checkin.userIds.includes(userId),
        )
        .sort((a, b) => (a.updatedAt > b.updatedAt ? -1 : 1))
        .slice(0, limit)
    },
    finalisedForAuthor(userId: string) {
      return store.filtered(
        (checkin) => checkin.author.id === userId && checkin.isFinalised,
      )
    },
    finalisedSortedByFinalisedAtDescForAuthor(userId: string) {
      return this.finalisedForAuthor(userId).sort((a, b) => {
        if (a.finalisedAt === null || b.finalisedAt === null) return -1

        return a.finalisedAt > b.finalisedAt ? -1 : 1
      })
    },
    forAuthor(userId: string) {
      return store.filtered(
        (checkin) => checkin.author.id === userId && !checkin.isArchived,
      )
    },
    forAssessor(userId: string) {
      return store.filtered(
        (checkin) =>
          checkin.assessor?.id === userId && checkin.author.id !== userId,
      )
    },
    forAuthors(userIds: string[]) {
      return store.filtered((checkin) => userIds.includes(checkin.author.id))
    },
    forAuthorAndPosition(userId: string, positionId: string) {
      return store.filtered(
        (checkin) =>
          checkin.author.id === userId && checkin.position?.id === positionId,
      )
    },
    forOrg(orgId: string) {
      return store.filtered((checkin) => checkin.author.org?.id === orgId)
    },
    lastFinalisedUserCheckin(userId: string) {
      const filtered = this.finalisedSortedByFinalisedAtDescForAuthor(userId)

      if (filtered.length > 0) return filtered[0]
    },
    previousFinalisedUserCheckin(checkin: Checkin) {
      if (!checkin.finalisedAt) return null
      const filtered = this.finalisedSortedByFinalisedAtDescForAuthor(
        checkin.author.id,
      ).filter(
        (c) =>
          c.finalisedAt &&
          checkin.finalisedAt &&
          c.finalisedAt < checkin.finalisedAt,
      )

      if (filtered.length > 0) return filtered[0]
    },
    lastUserCheckin(userId: string) {
      return store
        .filtered((checkin) => checkin.author.id === userId)
        .sort((a, b) => (a.updatedAt > b.updatedAt ? -1 : 1))[0]
    },
    notFinalisedForAuthor(userId: string) {
      return store.filtered(
        (checkin) => !checkin.isFinalised && checkin.author.id === userId,
      )
    },
    notFinalisedForAuthorAndPosition(userId: string, positionId: string) {
      return store.filtered(
        (checkin) =>
          !checkin.isFinalised &&
          checkin.author.id === userId &&
          checkin.position?.id === positionId,
      )
    },
    actionNeededFromUser(userId: string) {
      return store.filtered((checkin) => checkin.actionNeededFromUser(userId))
    },
  }))
